import React, { useState } from 'react';

import { Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useAccessControlPermissionsAdminQuery } from '@admin/schema';

import { AccessControlPermissionsAdminExplorer } from './permission_explorer_panel';
import { EmptyState, Header, Page, Subtitle, TabBar, TabButton, TabPanel, Title } from './permissions_admin_shared';
import { AccessControlPermissionsAdminRoles } from './role_permissions_panel';
import { AccessControlPermissionsAdminUsers } from './user_roles_panel';

enum View {
  Permission = 'permission',
  Role = 'role',
  User = 'user',
}

export const AccessControlPermissionsAdmin: React.FC = () => {
  useTitle('Permissions');
  const [view, setView] = useState<View>(View.Permission);

  const { data, loading, error, refetch } = useAccessControlPermissionsAdminQuery({
    client,
    fetchPolicy: 'network-only',
  });

  const admin = data?.accessControlPermissionsAdmin;
  const users = admin?.staffUsers ?? [];
  const roles = admin?.roles ?? [];
  const permissions = admin?.permissions ?? [];

  if (loading && !data) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Page>
        <Text style="danger">Failed to load access control settings: {error.message}</Text>
      </Page>
    );
  }

  if (!data || !admin || admin.roles.length === 0) {
    return (
      <Page>
        <Header>
          <Title>Permissions</Title>
          <Subtitle>No access roles are loaded yet.</Subtitle>
        </Header>
        <Panel>
          <Panel.Body>
            <EmptyState>
              Run <code>bundle exec rake access_control:sync_permissions</code>, then refresh this page.
            </EmptyState>
          </Panel.Body>
        </Panel>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <Title>Permissions</Title>
        <Subtitle>
          Browse and edit access by permission, role, or user. Grant roles to a permission, assign permissions to a
          role, or manage user role assignments kept in sync with job roles.
        </Subtitle>
      </Header>

      <TabBar>
        <TabButton active={view === View.Permission} onClick={() => setView(View.Permission)}>
          By Permission ({permissions.length})
        </TabButton>
        <TabButton active={view === View.Role} onClick={() => setView(View.Role)}>
          By Role ({roles.length})
        </TabButton>
        <TabButton active={view === View.User} onClick={() => setView(View.User)}>
          By User ({users.length})
        </TabButton>
      </TabBar>

      <TabPanel hidden={view !== View.Permission}>
        <AccessControlPermissionsAdminExplorer data={data} onSave={() => refetch()} />
      </TabPanel>
      <TabPanel hidden={view !== View.Role}>
        <AccessControlPermissionsAdminRoles data={data} onSave={() => refetch()} />
      </TabPanel>
      <TabPanel hidden={view !== View.User}>
        <AccessControlPermissionsAdminUsers data={data} onSave={() => refetch()} />
      </TabPanel>
    </Page>
  );
};
